import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Star, Phone, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

// Real customer reviews only - do not generate fake reviews
const reviews = [
  {
    id: 1,
    name: 'Aya',
    avatar: 'A',
    avatarColor: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
    rating: 5,
    date: 'Recent',
    featured: true,
    text: `The Thrifty Move team made my very difficult and stressful move so much easier!

From the initial inquiry and quote to the booking process, their communication was incredibly fast, professional, and reliable. I really appreciated the regular email confirmations leading up to the day, which gave me great peace of mind.

On moving day, the team started exactly on time and worked very efficiently. They even helped me with setting up things at my new place, including some DIY tasks that I couldn't do myself, which was a huge help. Everyone was polite, friendly, and left a wonderful impression.

Their pricing is also very reasonable. I would 100% recommend them to anyone planning a move. Thank you so much to the whole Thrifty Move team!`
  },
  {
    id: 2,
    name: 'Ion Raducan',
    avatar: 'I',
    avatarColor: 'bg-teal-500',
    rating: 5,
    date: '2 months ago',
    hasPhoto: true,
    photoUrl: 'https://customer-assets.emergentagent.com/job_quickquote-18/artifacts/1bhkf0bw_1%20rev%20thrifty.png',
    text: `Booked Thrifty move for my relocation from Dandenong to Bentleigh east, honestly it was the best decision, they got me a quote and prices were way less than other companies, they came right on time, team was polite and hard working!

I was genuinely impressed!
No hidden fees, no extra charges!

Really happy with Thrifty move, will recommend to everyone in the future!`
  },
  {
    id: 3,
    name: 'pinterwwa',
    avatar: 'P',
    avatarColor: 'bg-pink-400',
    rating: 5,
    date: '2 months ago',
    text: `Unfortunately, I needed to move my office closer to my suburb, so I asked Thrifty Move for help.

I was genuinely impressed with how fast and efficiently the team worked. They packed everything properly, handled all equipment with care, and had the whole move done without any issues.

Professional, organised, and very easy to deal with – great service overall.`
  },
  {
    id: 4,
    name: 'Sarah M.',
    avatar: 'S',
    avatarColor: 'bg-purple-500',
    rating: 5,
    date: '3 months ago',
    text: `Fantastic service! The team were professional, careful with our furniture, and very affordable. They made what could have been a stressful day so much easier. Highly recommend Thrifty Move to anyone in Melbourne!`
  },
  {
    id: 5,
    name: 'James T.',
    avatar: 'J',
    avatarColor: 'bg-blue-500',
    rating: 5,
    date: '3 months ago',
    text: `Used Thrifty Move for our office relocation. They were punctual, efficient, and made the whole process stress-free. Great communication throughout and reasonable pricing. Will definitely use again.`
  },
  {
    id: 6,
    name: 'Emma W.',
    avatar: 'E',
    avatarColor: 'bg-green-500',
    rating: 5,
    date: '4 months ago',
    text: `Best removalists in Melbourne! Great prices and they treated our belongings like their own. The guys were friendly, professional and worked hard. Would use again without hesitation.`
  }
];

export default function ReviewsPage() {
  const { reviews: reviewStats, contact } = businessConfig;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`Customer Reviews | ${reviewStats.displayText} | Thrifty Move Melbourne`}</title>
        <meta name="description" content={`Read what customers say about Thrifty Move Melbourne removalists. ${reviewStats.displayText}. Professional moving from $130/hr. Real reviews from real Melbourne moves.`} />
        <link rel="canonical" href="https://thrifty-move.com.au/reviews" />
        <meta property="og:title" content={`Customer Reviews | Thrifty Move Melbourne | From $130/hr`} />
        <meta property="og:description" content={`${reviewStats.displayText}. Professional removalists from $130/hr. See why Melbourne trusts Thrifty Move.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/reviews" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MovingCompany",
            "name": "Thrifty Move",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": reviewStats.rating.toString(),
              "reviewCount": reviewStats.count.toString(),
              "bestRating": "5"
            }
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-gray-900 pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-yellow-500 fill-current" />
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Customer Reviews
            </h1>
            <p className="text-2xl text-yellow-500 font-bold mb-2">
              {reviewStats.displayText}
            </p>
            <p className="text-gray-400">
              Real reviews from real Melbourne moves
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-yellow-500 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{reviewStats.count}+</div>
              <div className="text-gray-800 text-sm">Google Reviews</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{reviewStats.rating}</div>
              <div className="text-gray-800 text-sm">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">100%</div>
              <div className="text-gray-800 text-sm">Would Recommend</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Featured Review */}
            {reviews.filter(r => r.featured).map(review => (
              <Card key={review.id} className="border-2 border-yellow-400 shadow-lg mb-10">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-yellow-500 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">Featured Review</span>
                  </div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 ${review.avatarColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <span className="text-xl font-bold text-white">{review.avatar}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-lg text-gray-900">{review.name}</span>
                        <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">Verified</span>
                      </div>
                      <div className="flex text-yellow-500">
                        {[...Array(review.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                      </div>
                    </div>
                  </div>
                  <blockquote className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {review.text}
                  </blockquote>
                </CardContent>
              </Card>
            ))}

            {/* Other Reviews */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviews.filter(r => !r.featured).map(review => (
                <Card key={review.id} className="border border-gray-200 hover:border-yellow-400 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-11 h-11 ${review.avatarColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <span className="text-lg font-bold text-white">{review.avatar}</span>
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{review.name}</div>
                        <div className="flex items-center gap-2">
                          <div className="flex text-yellow-500">
                            {[...Array(review.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                          </div>
                          <span className="text-gray-400 text-xs">{review.date}</span>
                        </div>
                      </div>
                    </div>
                    {review.hasPhoto && review.photoUrl && (
                      <img 
                        src={review.photoUrl} 
                        alt={`Photo from ${review.name}'s review`}
                        className="w-full h-40 object-cover rounded-lg mb-3"
                      />
                    )}
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                      {review.text}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Google Link */}
            <div className="text-center mt-10">
              <a 
                href="https://www.google.com/search?q=thrifty+move+melbourne+reviews" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-gray-600 hover:text-yellow-600 font-medium"
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4 mr-2" />
                See all reviews on Google
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Experience the Thrifty Move Difference?
          </h2>
          <p className="text-gray-800 mb-8 max-w-xl mx-auto">
            Join our satisfied customers and get your free moving quote today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white font-bold px-10 py-5 text-lg">
                <ArrowRight className="mr-2 w-5 h-5" />
                Get a Free Quote
              </Button>
            </Link>
            <a href={contact.phoneLink} onClick={() => trackPhoneClick('Reviews - Call')}>
              <Button variant="outline" className="w-full sm:w-auto border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold px-10 py-5 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                {contact.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
